<template>
  <section v-if="config" ref="card" class="dashboard-video" aria-labelledby="dashboard-video-title">
    <button
      ref="toggle"
      type="button"
      class="dashboard-video__toggle"
      :aria-expanded="expanded"
      aria-controls="dashboard-video-player"
      :aria-label="`${expanded ? 'Chiudi' : 'Riproduci'}: ${config.title}`"
      @click="expanded ? closeVideo() : openVideo(false)"
    >
      <span class="dashboard-video__preview" aria-hidden="true">
        <img v-if="config.posterUrl" :src="config.posterUrl" alt="" loading="lazy" />
        <span v-else class="material-icons">smart_display</span>
      </span>
      <span class="dashboard-video__copy">
        <span class="dashboard-video__eyebrow">Dal team Fusco Fit</span>
        <span id="dashboard-video-title" class="dashboard-video__title">{{ config.title }}</span>
        <span class="dashboard-video__subtitle">{{ config.subtitle }}</span>
      </span>
      <span class="dashboard-video__action material-icons" aria-hidden="true">{{ expanded ? 'close' : 'play_arrow' }}</span>
    </button>

    <Transition name="dashboard-video-expand">
      <div
        v-if="expanded"
        id="dashboard-video-player"
        class="dashboard-video__reveal"
        :inert="!expanded || undefined"
      >
        <div class="dashboard-video__reveal-inner">
          <div class="dashboard-video__body">
            <div class="dashboard-video__frame">
              <video
                ref="player"
                :src="config.videoUrl"
                :poster="config.posterUrl || undefined"
                :muted="muted"
                :aria-label="config.title"
                controls
                playsinline
                preload="none"
                @playing="onPlaying"
                @volumechange="syncMuted"
                @ended="closeVideo(true)"
                @error="onPlaybackError"
              ></video>
              <div v-if="playbackError" class="dashboard-video__feedback" role="status">
                <span class="material-icons" aria-hidden="true">videocam_off</span>
                <p>Il video non è disponibile al momento.</p>
                <button type="button" @click="retryVideo">Riprova</button>
              </div>
              <button v-else-if="needsPlay" type="button" class="dashboard-video__start" @click="startWithAudio">
                <span class="material-icons" aria-hidden="true">play_arrow</span>
                Riproduci video
              </button>
            </div>
            <div class="dashboard-video__footer">
              <a
                v-if="config.link"
                class="dashboard-video__link"
                :href="config.link"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="`${config.linkLabel} (si apre in una nuova finestra)`"
                @click="pauseForLink"
              >
                <span>{{ config.linkLabel }}</span>
                <span class="material-icons" aria-hidden="true">arrow_outward</span>
              </a>
              <div class="dashboard-video__footer-controls">
                <button v-if="muted && !playbackError" type="button" @click="startWithAudio">
                  <span class="material-icons" aria-hidden="true">volume_up</span>
                  Attiva audio
                </button>
                <span v-else>Puoi rivederlo quando vuoi.</span>
                <button type="button" @click="closeVideo(true)">Chiudi video</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDocFromServer, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import {
  DASHBOARD_VIDEO_PATH, normalizeDashboardVideo, hasLocalVideoReceipt,
  saveLocalVideoReceipt, withVideoTimeout,
} from '@/lib/dashboardVideo';

export default {
  name: 'DashboardVideoCard',
  setup() {
    const config = ref(null);
    const card = ref(null);
    const toggle = ref(null);
    const player = ref(null);
    const expanded = ref(false);
    const muted = ref(true);
    const needsPlay = ref(false);
    const playbackError = ref(false);
    let uid = '';
    let generation = 0;
    let autoPending = false;
    let inView = false;
    let destroyed = false;
    let observer;
    let unsubscribeAuth;
    let unsubscribeConfig;
    let lastServerConfig = '';
    const shownThisSession = new Set();
    const pendingReceipts = new Map();
    let storage;
    try { storage = window.localStorage; } catch (_) { /* Optional local fallback. */ }

    const receiptRef = (userId, version) => doc(db, 'dashboardVideoViews', userId, 'versions', version);
    const receiptKey = (version) => `${uid}:${version}`;

    function persistReceipt(version) {
      if (!uid || !version) return;
      const key = receiptKey(version);
      if (shownThisSession.has(key)) return;
      shownThisSession.add(key);
      saveLocalVideoReceipt(storage, uid, version);
      const userId = uid;
      pendingReceipts.set(key, { userId, version });
      // Persist only the fact that the card was shown, not a claim that it was watched.
      setDoc(receiptRef(userId, version), { shownAt: serverTimestamp() })
        .then(() => pendingReceipts.delete(key))
        .catch(() => { /* Retried on reconnect; local receipt still prevents repetition. */ });
    }

    function stopPlayer() {
      const video = player.value;
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    function collapse() {
      autoPending = false;
      stopPlayer();
      expanded.value = false;
      needsPlay.value = false;
      playbackError.value = false;
    }

    function closeVideo(restoreFocus = false) {
      if (config.value) persistReceipt(config.value.version);
      collapse();
      if (restoreFocus) nextTick(() => toggle.value?.focus({ preventScroll: true }));
    }

    async function playVideo() {
      const video = player.value;
      if (!video) return;
      needsPlay.value = false;
      try {
        await video.play();
      } catch (_) {
        // Ignore a pending play promise cancelled by closing/navigating/changing video.
        if (player.value === video && expanded.value && !playbackError.value) needsPlay.value = true;
      }
    }

    async function openVideo(automatic) {
      if (!config.value || expanded.value || destroyed) return;
      autoPending = false;
      muted.value = automatic;
      playbackError.value = false;
      needsPlay.value = false;
      expanded.value = true;
      persistReceipt(config.value.version);
      const currentGeneration = generation;
      await nextTick();
      if (destroyed || generation !== currentGeneration || !player.value) return;
      player.value.muted = automatic;
      // Manual clicks retain user activation through Vue's microtask update.
      playVideo();
    }

    function maybeAutoOpen() {
      if (autoPending && inView && !document.hidden) openVideo(true);
    }

    async function acceptConfig(snapshot) {
      const value = normalizeDashboardVideo(snapshot.exists() ? snapshot.data() : null);
      const identity = JSON.stringify(value);
      if (snapshot.metadata.fromCache) {
        // A cached document may be stale: show a compact card, never auto-play it.
        if (!lastServerConfig) config.value = value;
        return;
      }
      if (identity === lastServerConfig) return;
      lastServerConfig = identity;
      const currentGeneration = ++generation;
      // A server confirmation of the same cached video must not interrupt a manual replay.
      const mediaChanged = !value || value.version !== config.value?.version
        || value.videoUrl !== config.value?.videoUrl;
      autoPending = false;
      if (mediaChanged) collapse();
      config.value = value;
      if (!value || !uid || !value.autoOpen) return;
      const userId = uid;
      const localSeen = hasLocalVideoReceipt(storage, uid, value.version);
      try {
        const receipt = await withVideoTimeout(getDocFromServer(receiptRef(uid, value.version)));
        if (destroyed || generation !== currentGeneration || userId !== uid) return;
        if (receipt.exists() || localSeen || shownThisSession.has(receiptKey(value.version))) {
          if (receipt.exists()) saveLocalVideoReceipt(storage, uid, value.version);
          // Reconcile a receipt originally recorded offline without auto-opening.
          else persistReceipt(value.version);
          return;
        }
        autoPending = true;
        maybeAutoOpen();
      } catch (_) {
        // If first-access state cannot be confirmed, stay compact and allow manual play.
      }
    }

    function startWithAudio() {
      muted.value = false;
      if (player.value) player.value.muted = false;
      playVideo();
    }

    function pauseForLink() {
      player.value?.pause();
    }

    function retryVideo() {
      playbackError.value = false;
      player.value?.load();
      startWithAudio();
    }

    function onPlaybackError(event) {
      if (!expanded.value || (event?.target && event.target !== player.value)) return;
      playbackError.value = true;
      needsPlay.value = false;
    }

    function onPlaying() { needsPlay.value = false; }
    function syncMuted() { muted.value = player.value?.muted ?? true; }

    function onVisibilityChange() {
      if (document.hidden) player.value?.pause();
      else maybeAutoOpen();
    }

    function retryReceipts() {
      for (const [key, receipt] of pendingReceipts) {
        if (receipt.userId !== uid) continue;
        setDoc(receiptRef(uid, receipt.version), { shownAt: serverTimestamp() })
          .then(() => pendingReceipts.delete(key)).catch(() => {});
      }
    }

    watch(card, (element) => {
      observer?.disconnect();
      inView = false;
      if (!element) return;
      if (!('IntersectionObserver' in window)) { inView = true; maybeAutoOpen(); return; }
      observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        maybeAutoOpen();
      }, { threshold: 0.25 });
      observer.observe(element);
    });

    onMounted(() => {
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('online', retryReceipts);
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        ++generation;
        unsubscribeConfig?.();
        collapse();
        config.value = null;
        lastServerConfig = '';
        uid = user?.uid || '';
        if (!uid) return;
        unsubscribeConfig = onSnapshot(doc(db, DASHBOARD_VIDEO_PATH), { includeMetadataChanges: true }, acceptConfig, () => {
          ++generation;
          collapse();
          config.value = null;
        });
      });
    });

    onBeforeUnmount(() => {
      destroyed = true;
      ++generation;
      collapse();
      unsubscribeAuth?.();
      unsubscribeConfig?.();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', retryReceipts);
    });

    return { config, card, toggle, player, expanded, muted, needsPlay, playbackError,
      openVideo, closeVideo, startWithAudio, pauseForLink, retryVideo, onPlaybackError, onPlaying, syncMuted };
  },
};
</script>

<style scoped>
.dashboard-video,
.dashboard-video * { box-sizing: border-box; }
.dashboard-video {
  margin-top: clamp(20px, 3vw, 32px);
  min-width: 0;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 24px;
  background: linear-gradient(120deg, #fff4f5, #fff 65%);
  box-shadow: 0 16px 44px rgba(17, 17, 16, 0.08);
  overflow: hidden;
  color: #111110;
}
.dashboard-video button { font: inherit; cursor: pointer; }
.dashboard-video button:focus-visible, .dashboard-video a:focus-visible { outline: 3px solid #d00032; outline-offset: -4px; }
.dashboard-video__toggle {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 44px;
  gap: 16px;
  align-items: center;
  width: 100%;
  padding: 20px;
  border: 0;
  text-align: left;
  color: inherit;
  background: transparent;
}
.dashboard-video__preview {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(208, 0, 50, 0.15);
  border-radius: 16px;
  background: rgba(208, 0, 50, 0.07);
  overflow: hidden;
}
.dashboard-video__preview > .material-icons { color: #d00032; font-size: 28px; }
.dashboard-video__preview img { display: block; width: 100%; height: 100%; object-fit: cover; }
.dashboard-video__copy { display: grid; gap: 4px; min-width: 0; overflow-wrap: anywhere; }
.dashboard-video__eyebrow { color: #ca0030; font-size: 10px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
.dashboard-video__title { font-size: clamp(17px, 2vw, 22px); font-weight: 800; line-height: 1.15; letter-spacing: -0.035em; }
.dashboard-video__subtitle { color: #676763; font-size: 13px; line-height: 1.4; }
.dashboard-video__action.material-icons {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #d00032;
  color: #fff;
  font-size: 26px;
}
.dashboard-video__reveal { display: grid; grid-template-rows: 1fr; opacity: 1; }
.dashboard-video__reveal-inner { min-height: 0; overflow: hidden; }
.dashboard-video-expand-enter-active {
  transition: grid-template-rows 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease-out;
}
.dashboard-video-expand-leave-active {
  transition: grid-template-rows 240ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease-in;
  pointer-events: none;
}
/* Releasing the source can briefly expose the browser's empty-media message. */
.dashboard-video-expand-leave-active video { visibility: hidden; }
.dashboard-video-expand-enter-from,
.dashboard-video-expand-leave-to { grid-template-rows: 0fr; opacity: 0; }
.dashboard-video__body { border-top: 1px solid rgba(17, 17, 16, 0.08); padding: 16px; }
.dashboard-video__frame {
  position: relative;
  width: 100%;
  max-width: 560px;
  aspect-ratio: 1;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  background: #111110;
}
.dashboard-video__frame video { display: block; width: 100%; height: 100%; object-fit: contain; }
.dashboard-video__footer { display: grid; gap: 10px; max-width: 560px; margin: 10px auto 0; font-size: 12px; color: #676763; }
.dashboard-video__footer-controls { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
.dashboard-video__footer button { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; padding: 8px 10px; border: 0; border-radius: 10px; background: #f6f1f2; color: #222; font-size: 13px; font-weight: 700; }
.dashboard-video__footer .material-icons { color: #d00032; font-size: 20px; }
.dashboard-video__footer .dashboard-video__link { display: flex; align-items: center; justify-content: center; gap: 12px; min-width: 0; min-height: 48px; padding: 12px 18px; border-radius: 12px; background: #d00032; color: #fff; text-decoration: none; font-size: 14px; font-weight: 800; line-height: 1.4; text-align: center; }
.dashboard-video__link > span:first-child { min-width: 0; overflow-wrap: anywhere; }
.dashboard-video__link .material-icons { flex-shrink: 0; color: #fff; }
.dashboard-video__link:hover { background: #b5002c; }
.dashboard-video .dashboard-video__link:focus-visible { outline-color: #111110; outline-offset: 3px; }
.dashboard-video__feedback { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; text-align: center; color: #fff; background: #111110; }
.dashboard-video__feedback p { font-size: 15px; line-height: 1.5; }
.dashboard-video__feedback .material-icons { color: #fff; font-size: 32px; }
.dashboard-video__feedback button,
.dashboard-video__start { min-height: 44px; padding: 12px 18px; border: 1px solid rgba(255,255,255,0.5); border-radius: 100px; color: #fff; background: #d00032; font-weight: 700; }
.dashboard-video__start { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; gap: 8px; width: max-content; max-width: calc(100% - 24px); font-size: 14px; }
.dashboard-video__start .material-icons { color: #fff; }
@media (prefers-reduced-motion: reduce) {
  .dashboard-video-expand-enter-active,
  .dashboard-video-expand-leave-active { transition: none; }
}
@media (max-width: 420px) {
  .dashboard-video__toggle { grid-template-columns: 44px minmax(0, 1fr) 44px; padding: 16px 12px; gap: 10px; }
  .dashboard-video__preview { width: 44px; height: 44px; border-radius: 13px; }
  .dashboard-video__subtitle { font-size: 12px; }
  .dashboard-video__body { padding: 10px; }
}
</style>
